import React from 'react';
import { Briefcase, MapPin } from 'lucide-react';

const ProfessionalJourney = () => {
  const experiences = [
    {
      title: 'School Inclusion Specialist',
      organization: 'Department of Education',
      location: 'Cayman Islands',
      period: 'August 2024 – Present',
      description: 'Leading government-wide behavioural and social-emotional interventions, mentoring teaching staff, and redesigning SEMH documentation systems at the national level.',
      highlights: [
        'Deliver targeted interventions enabling at-risk students to thrive academically and socially',
        'Mentor and train teachers in evidence-based classroom management and differentiation strategies',
        'Lead multi-agency teams ensuring coordinated, holistic student support',
        'Redesign national SEMH documentation systems improving reporting and accountability'
      ]
    },
    {
      title: 'Principal',
      organization: 'The Casco School',
      location: 'Panama City, Panama',
      period: 'August 2023 – 2024',
      description: 'Provided strategic leadership for three primary school sites, driving excellence in teaching, learning, and safeguarding while managing operations, budgets, and stakeholder relations.',
      highlights: [
        'Led three primary school sites serving 300+ students with diverse learning needs',
        'Served as SENCO and Designated Safeguarding Lead, establishing unified child protection frameworks',
        'Implemented comprehensive safeguarding training and protocols across all sites',
        'Developed cross-site professional development initiatives improving teaching quality',
        'Managed multi-site budgets, staffing, and operational systems'
      ]
    },
    {
      title: 'Assistant Head Teacher',
      organization: 'Western Primary School',
      location: 'Grimsby, UK',
      period: 'August 2013 – August 2022',
      description: 'Senior leadership role driving whole-school improvement, curriculum development, and safeguarding. Led COVID-19 crisis response earning MBE recognition. Managed budgets, staff teams, and assessment systems while maintaining high academic standards.',
      highlights: [
        'Awarded MBE for leading strategic COVID-19 response ensuring continuity of education and welfare for 110+ vulnerable families',
        'Directed curriculum development and teaching standards across Key Stage 2, achieving consistent "Good" and "Outstanding" ratings',
        'Implemented data-driven assessment and tracking systems enabling targeted interventions and measurable improvement',
        'Managed departmental budgets, staffing allocations, and resource optimization',
        'Collaborated with educational psychologists and SEND specialists developing school-wide behavioural and inclusion strategies'
      ],
      featured: true
    },
    {
      title: 'Panel Member - Strategic Inclusion & Decision-Making Bodies',
      organization: 'Behaviour and Attendance Collaborative (BAC) & SENAG',
      location: 'Grimsby, UK',
      period: '2011 - 2020',
      description: 'Appointed to two high-level decision-making panels; The Behaviour and Attendance Collaborative (BAC) and the Education, Health and Care Plan (EHCP) / Special Educational Needs Action Group (SENAG) — in recognition of specialist expertise in SEND, SEMH, and Behaviour Management.',
      highlights: [
        'BAC Panel: Supported schools managing pupils with complex, high-risk behavioural needs through in-situ observations',
        'Provided senior leaders with detailed evidence-based reports on inclusion and regulation strategies',
        'Led transitions to The Resource Base for stabilisation, therapeutic support, and successful mainstream reintegration',
        'EHCP/SENAG Panel: Evaluated multi-agency evidence against statutory criteria (Children and Families Act 2014, SEND Code of Practice 2015)',
        'Determined funding banding and identified appropriate educational placements; from mainstream to specialist provision',
        'Interpreted complex legal frameworks ensuring lawful, equitable, needs-led decisions',
        'Safeguarded every child\'s right to appropriate support while strengthening school capacity for early intervention'
      ],
      featured: true
    },
    {
      title: 'Principal of Resource Base',
      organization: 'Western Primary School',
      location: 'Grimsby, UK',
      period: 'August 2013 - August 2022',
      description: 'Led growth and operational management of a Pupil Referral Unit for children with severe SEN and behavioural needs.',
      highlights: [
        'Implemented policies for children with severe SEN',
        'Reduced exclusion risk for underserved children',
        'Collaborated with educational psychologists',
        'Trained educational leaders on SEMH challenges'
      ]
    },
    {
      title: 'Year 6 Teacher / Key Stage Leader',
      organization: 'Western Primary School',
      location: 'Grimsby, UK',
      period: 'August 2011 - August 2013',
      description: 'Directed Key Stage 2 team, maintaining high standards in teaching and learning.',
      highlights: [
        'Led Key Stage 2 team with high teaching standards',
        'Mentored Newly Qualified Teachers',
        'Served as Lead SATs Writing Moderator'
      ]
    },
    {
      title: 'Class Teacher (Years 1, 4 & 6) / Key Stage Leader',
      organization: 'Nusthorpe Community School',
      location: 'Grimsby, UK',
      period: 'January 2006 - August 2011',
      description: 'First teaching position following completion of B.A. (Hons) Primary Education with QTS. Progressed to Key Stage Leader by third year, demonstrating rapid professional development and teaching excellence across multiple year groups.',
      highlights: [
        'Taught across Years 1, 4, and 6, adapting pedagogy for diverse age groups and learning needs',
        'Promoted to Key Stage Leader in third year of teaching, leading curriculum planning and assessment',
        'Served as Local Authority KS2 Writing Moderator for over 60 Local Authority and Academy Schools, meeting the SATs criteria for writing',
        'Developed strong classroom management skills and built positive relationships with pupils, parents, and colleagues',
        'Established foundation for inclusive teaching practices that would define later career',
        'Contributed to whole-school improvement initiatives and staff professional development'
      ]
    },
    {
      title: 'Veteran (Grenadier Guards)',
      organization: 'British Army',
      location: 'United Kingdom',
      period: '1988-1997',
      description: 'Served with distinction in the elite Grenadier Guards, developing leadership, resilience, and discipline.',
      highlights: [
        'Elite military service with Grenadier Guards',
        'Developed leadership and discipline skills',
        'Served with honour and distinction'
      ],
      military: true
    }
  ];

  return (
    <section id="professional-journey" className="journey-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Professional Journey</h2>
          <p className="section-subtitle">From military service to educational leadership across three continents</p>
        </div>

        <div className="timeline">
          {experiences.map((exp, index) => (
            <div key={index} className={`timeline-item ${exp.featured ? 'featured' : ''} ${exp.military ? 'military' : ''}`}>
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-title">{exp.title}</h3>
                    <p className="timeline-org">{exp.organization}</p>
                  </div>
                  <div className="timeline-meta">
                    <span className="timeline-location">
                      <MapPin size={16} />
                      {exp.location}
                    </span>
                    <span className="timeline-period">{exp.period}</span>
                  </div>
                </div>
                <p className="timeline-description">{exp.description}</p>
                <ul className="timeline-highlights">
                  {exp.highlights.map((highlight, idx) => (
                    <li key={idx}>{highlight}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProfessionalJourney;